import React from "react";
import applyClassNames from "classnames";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import type { Translatable } from "app/pages/sales-intelligence/types";
import type { SelectionDropdownItem } from "../../models/selection-dropdown-item/types";
import SelectionDropdownButton from "./SelectionDropdownButton/SelectionDropdownButton";
import { StyledDDContainer, StyledContent, StyledItemsContainer, StyledFooterContainer } from "./styles";

type SelectionDropdownProps = {
    buttonText: Readonly<Translatable>;
    className?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    footer?: React.ReactNode;
    isHideFooterDivider?: boolean;
    children: React.ReactElement[];
    onSelect(item: SelectionDropdownItem): void;
};

const SelectionDropdownView = (props: SelectionDropdownProps) => {
    const CONTAINER_CLASSNAME = "selection-dd-container";
    const {
        buttonText,
        footer,
        isHideFooterDivider,
        children,
        className = null,
        isLoading = false,
        isDisabled = false,
        onSelect,
    } = props;
    const [isOpened, setIsOpened] = React.useState(false);

    const closeDropdown = React.useCallback(() => setIsOpened(false), []);

    const selectAndClose = React.useCallback(
        (item: SelectionDropdownItem) => {
            closeDropdown();
            onSelect(item);
        },
        [onSelect],
    );

    useDeactivateOnOutsideClick(CONTAINER_CLASSNAME, isOpened, closeDropdown);

    return (
        <StyledDDContainer
            className={applyClassNames(CONTAINER_CLASSNAME, className)}
            data-automation="table-actions-panel.selection-dropdown"
            data-automation-opened={isOpened}
        >
            <SelectionDropdownButton
                text={buttonText}
                isDisabled={isDisabled || isLoading}
                onClick={() => setIsOpened(true)}
            />
            <StyledContent open={isOpened}>
                <StyledItemsContainer>
                    {React.Children.map(children, (child) => {
                        return React.cloneElement(child, {
                            // @ts-expect-error error TS2698: Spread types may only be created from object types.
                            ...child.props,
                            onClick: selectAndClose,
                        });
                    })}
                </StyledItemsContainer>
                {typeof footer !== "undefined" ? (
                    <StyledFooterContainer isHideFooterDivider={isHideFooterDivider}>{footer}</StyledFooterContainer>
                ) : null}
            </StyledContent>
        </StyledDDContainer>
    );
};

export default SelectionDropdownView;
